import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Funnel } from "lucide-react";
import { toast } from "react-toastify";
import { getOrgUsersApi, updateUserStatusApi } from "../../api/users/userApi";
import { useUser } from "../../context/UserContext";
import { canManageOrgUsers } from "../../utils/roles";

interface ApiUser {
  org_id: number;
  user_guid: string;
  user_id: number;
  name: string;
  phone: string;
  email: string | null;
  wallet_balance: number;
  vehicle_name: string | null;
  status: string;
}

interface OrganizationUsersProps {
  onPendingClick: () => void;
}

const PAGE_SIZE = 20;
const DEBOUNCE_DELAY = 500;

const OrganizationUsers: React.FC<OrganizationUsersProps> = ({
  onPendingClick,
}) => {
  const { user } = useUser();

  const [users, setUsers] = useState<ApiUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Filter states for each column
  const [filters, setFilters] = useState<{ [key: string]: string }>({});
  const [debouncedFilters, setDebouncedFilters] = useState<{ [key: string]: string }>({});
  const [activeFilterColumn, setActiveFilterColumn] = useState<string | null>(null);

  // Refs for debounce timers
  const searchTimerRef = useRef<NodeJS.Timeout | null>(null);
  const filterTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Disable/enable modal state
  const [confirmUser, setConfirmUser] = useState<ApiUser | null>(null);
  const [confirmReason, setConfirmReason] = useState("");
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  /* =====================
     DEBOUNCE SEARCH INPUT
     ===================== */
  useEffect(() => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1); 
    }, DEBOUNCE_DELAY);

    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, [search]);

  /* =====================
     DEBOUNCE FILTER INPUT
     ===================== */
  useEffect(() => {
    if (filterTimerRef.current) {
      clearTimeout(filterTimerRef.current);
    }

    filterTimerRef.current = setTimeout(() => {
      setDebouncedFilters(filters);
      setPage(1);
    }, DEBOUNCE_DELAY);

    return () => {
      if (filterTimerRef.current) {
        clearTimeout(filterTimerRef.current);
      }
    };
  }, [filters]);

  /* =====================
     SORT QUERY (DOC)
     ===================== */
  const sortQuery = useMemo(() => {
    if (!sortField) return undefined;
    return sortOrder === "desc" ? `-${sortField}` : sortField;
  }, [sortField, sortOrder]);

  /* =====================
     FILTER QUERY (OPTIONAL)
     ===================== */
  const filterQuery = useMemo(() => {
    const filterConditions: string[] = [];
    
    Object.entries(debouncedFilters).forEach(([field, value]) => {
      if (value && value.trim() !== "") {
        if (field === 'wallet_balance') {
          // For wallet balance, use comparison operators
          if (value === '>100') {
            filterConditions.push(`${field}>100`);
          } else if (value === '<100') {
            filterConditions.push(`${field}<100`);
          }
        } else if (field === 'status') {
          // For status, use equals operator
          filterConditions.push(`${field}=${value}`);
        } else {
          // For text fields, use contains
          filterConditions.push(`${field}~${value}`);
        }
      }
    });

    if (filterConditions.length === 0) return undefined;
    
    // Join multiple conditions with AND
    return filterConditions.join(' AND ');
  }, [debouncedFilters]);

  /* =====================
     FETCH USERS (API)
     ===================== */
  useEffect(() => {
    if (!user?.org_id) return;

    const tenantId = user.org_id;

    const fetchUsers = async () => {
      try {
        setLoading(true);

        const response = await getOrgUsersApi({
          tenantId,
          page,
          page_size: PAGE_SIZE,
          search: debouncedSearch || undefined,
          sort: sortQuery,
          filter: filterQuery,
        });

        setUsers(response.users || []);
        setTotal(response.total || 0);
      } catch (e) {
        console.error("Failed to fetch users", e);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [user?.org_id, page, debouncedSearch, sortQuery, filterQuery]);

  /* =====================
     SORT HANDLER
     ===================== */
  const onSort = (field: string) => {
    setPage(1);
    setSortOrder((prev) =>
      sortField === field ? (prev === "asc" ? "desc" : "asc") : "asc"
    );
    setSortField(field);
  };

  /* =====================
     FILTER HANDLERS
     ===================== */
  const handleFilterChange = useCallback((field: string, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleFilterClear = useCallback((field: string) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[field];
      return newFilters;
    });
    setDebouncedFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[field];
      return newFilters;
    });
    setPage(1);
  }, []);

  const handleDropdownSelect = useCallback((field: string, value: string) => {
    if (value === '') {
      handleFilterClear(field);
    } else {
      setFilters((prev) => ({
        ...prev,
        [field]: value,
      }));
      setDebouncedFilters((prev) => ({
        ...prev,
        [field]: value,
      }));
      setPage(1);
    }
    setActiveFilterColumn(null);
  }, [handleFilterClear]);

  const toggleFilterDropdown = useCallback((field: string) => {
    setActiveFilterColumn((prev) => prev === field ? null : field);
  }, []);

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return <span className="sort">⇅</span>;
    return sortOrder === "asc" ? (
      <span className="sort">↑</span>
    ) : (
      <span className="sort">↓</span>
    );
  };

  const FilterIcon = ({ field }: { field: string }) => {
    const isActive = filters[field] && filters[field].trim() !== "";
    return (
      <span 
        className={`filter-icon ${isActive ? 'active' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          toggleFilterDropdown(field);
        }}
      >
        <Funnel size={14} />
      </span>
    );
  };

  const FilterDropdown = ({ field }: { field: string }) => {
    if (activeFilterColumn !== field) return null;

    if (field === 'status') {
      const statusOptions = [
        { value: '', label: 'All Statuses' },
        { value: 'active', label: 'Active' },
        { value: 'disabled', label: 'Disabled' },
        { value: 'suspended', label: 'Suspended' },
        { value: 'pending', label: 'Pending' },
      ];

      return (
        <div 
          className="filter-dropdown"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="filter-dropdown-content">
            {statusOptions.map((option) => (
              <div
                key={option.value}
                className={`filter-option ${filters[field] === option.value ? 'selected' : ''}`}
                onClick={() => handleDropdownSelect(field, option.value)}
              >
                {option.label}
              </div>
            ))}
          </div>
        </div>
      );
    }
    if (field === 'wallet_balance') {
      const walletOptions = [
        { value: '', label: 'All Balances' },
        { value: '>100', label: 'More than ₹100' },
        { value: '<100', label: 'Less than ₹100' },
      ];

      return (
        <div 
          className="filter-dropdown"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="filter-dropdown-content">
            {walletOptions.map((option) => (
              <div
                key={option.value}
                className={`filter-option ${filters[field] === option.value ? 'selected' : ''}`}
                onClick={() => handleDropdownSelect(field, option.value)}
              >
                {option.label}
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div 
        className="filter-dropdown"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="text"
          placeholder={`Filter ${field}...`}
          value={filters[field] || ""}
          onChange={(e) => handleFilterChange(field, e.target.value)}
          autoFocus
        />
        {filters[field] && (
          <button 
            className="clear-filter-btn"
            onClick={() => handleFilterClear(field)}
          >
            Clear
          </button>
        )}
      </div>
    );
  };

  const openConfirm = (u: ApiUser) => {
    setConfirmUser(u);
    setConfirmReason("");
  };

  const closeConfirm = () => {
    setConfirmUser(null);
    setConfirmReason("");
    setConfirmSubmitting(false);
  };

  const handleConfirmToggle = async () => {
    if (!confirmUser || !user?.org_id) return;
    const newStatus: "active" | "disabled" =
      confirmUser.status === "active" ? "disabled" : "active";
    setConfirmSubmitting(true);
    try {
      await updateUserStatusApi(user.org_id, confirmUser.user_id, {
        status: newStatus,
        ...(confirmReason.trim() ? { reason: confirmReason.trim() } : {}),
      });
      // Update the row in place so the UI reflects the new state immediately
      setUsers((prev) =>
        prev.map((x) =>
          x.user_id === confirmUser.user_id ? { ...x, status: newStatus } : x,
        ),
      );
      toast.success(
        newStatus === "disabled"
          ? `${confirmUser.name || confirmUser.phone} has been disabled`
          : `${confirmUser.name || confirmUser.phone} has been re-enabled`,
      );
      closeConfirm();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || "Failed to update user status";
      toast.error(detail);
      setConfirmSubmitting(false);
    }
  };

  return (
    <div className="org-users">
      <div className="org-users__header-row">
        <h2 className="org-users__title">Organization users</h2>

        <div className="org-users__actions">
          <input
            className="search-input"
            placeholder="Search by name, phone, email, vehicle..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
          />

          <button className="primary-btn" onClick={onPendingClick}>
            Pending Requests
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loader-container">
          <div className="loader" />
        </div>
      ) : (
        <>
          <div className="org-users__table-wrapper">
            <table className="org-users__table">
              <thead>
                <tr>
                  <th>
                    <div className="th-content">
                      <span className="th-label" onClick={() => onSort("name")}>
                        Name <SortIcon field="name" />
                      </span>
                      <FilterIcon field="name" />
                    </div>
                    <FilterDropdown field="name" />
                  </th>
                  <th>
                    <div className="th-content">
                      <span className="th-label" onClick={() => onSort("phone")}>
                        Phone <SortIcon field="phone" />
                      </span>
                      <FilterIcon field="phone" />
                    </div>
                    <FilterDropdown field="phone" />
                  </th>
                  <th>
                    <div className="th-content">
                      <span className="th-label" onClick={() => onSort("email")}>
                        Email <SortIcon field="email" />
                      </span>
                      <FilterIcon field="email" />
                    </div>
                    <FilterDropdown field="email" />
                  </th>
                  <th>
                    <div className="th-content">
                      <span className="th-label" onClick={() => onSort("vehicle_name")}>
                        Vehicle ID <SortIcon field="vehicle_name" />
                      </span>
                      <FilterIcon field="vehicle_name" />
                    </div>
                    <FilterDropdown field="vehicle_name" />
                  </th>
                  <th>
                    <div className="th-content">
                      <span className="th-label" onClick={() => onSort("wallet_balance")}>
                        Wallet Balance <SortIcon field="wallet_balance" />
                      </span>
                      <FilterIcon field="wallet_balance" />
                    </div>
                    <FilterDropdown field="wallet_balance" />
                  </th>
                  <th>
                    <div className="th-content">
                      <span className="th-label" onClick={() => onSort("status")}>
                        Status <SortIcon field="status" />
                      </span>
                      <FilterIcon field="status" />
                    </div>
                    <FilterDropdown field="status" />
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {users.map((u) => (
                  <tr key={u.user_guid}>
                    <td>{u.name}</td>
                    <td>{u.phone}</td>
                    <td>{u.email || "-"}</td>
                    <td>{u.vehicle_name || "-"}</td>
                    <td>₹ {u.wallet_balance.toFixed(2)}</td>
                    <td>
                      <span className={`status ${u.status}`}>{u.status}</span>
                    </td>
                    <td>
                      {canManageOrgUsers() && u.status === "active" ? (
                        <button
                          className="secondary-btn"
                          style={{ color: "#c62828", borderColor: "#c62828" }}
                          onClick={() => openConfirm(u)}
                          title="Disable this user's login"
                        >
                          Disable
                        </button>
                      ) : canManageOrgUsers() && u.status === "disabled" ? (
                        <button
                          className="secondary-btn"
                          onClick={() => openConfirm(u)}
                          title="Re-enable this user's login"
                        >
                          Enable
                        </button>
                      ) : (
                        <span style={{ color: "#999", fontSize: "13px" }}>—</span>
                      )}
                    </td>
                  </tr>
                ))}

                {users.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ textAlign: "center" }}>
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {confirmUser && (
            <div className="modal-overlay" onClick={closeConfirm}>
              <div className="modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal__header">
                  <h3>
                    {confirmUser.status === "active" ? "Disable user?" : "Re-enable user?"}
                  </h3>
                  <button className="close-btn" onClick={closeConfirm}>×</button>
                </div>
                <div className="modal__body">
                  <p>
                    {confirmUser.status === "active" ? (
                      <>
                        <strong>{confirmUser.name || confirmUser.phone}</strong> will no
                        longer be able to log in. Any current session will be blocked
                        on their next request. You can re-enable them later.
                      </>
                    ) : (
                      <>
                        <strong>{confirmUser.name || confirmUser.phone}</strong> will
                        be able to log in again.
                      </>
                    )}
                  </p>
                  <div className="form-group">
                    <label>Reason (optional)</label>
                    <input
                      placeholder="e.g. left the organization"
                      value={confirmReason}
                      onChange={(e) => setConfirmReason(e.target.value)}
                    />
                  </div>
                </div>
                <div className="modal__footer">
                  <button className="secondary-btn" onClick={closeConfirm} disabled={confirmSubmitting}>
                    Cancel
                  </button>
                  <button
                    className="primary-btn"
                    onClick={handleConfirmToggle}
                    disabled={confirmSubmitting}
                    style={confirmUser.status === "active" ? { background: "#c62828" } : undefined}
                  >
                    {confirmSubmitting
                      ? "Working..."
                      : confirmUser.status === "active"
                      ? "Disable"
                      : "Enable"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Pagination only meaningful after results */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                ‹
              </button>

              <span>
                Page {page} of {totalPages}
              </span>

              <button
                disabled={page === totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                ›
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OrganizationUsers;